/**
 * API Client for InstaCar Backend
 * Handles all HTTP requests to the Flask server
 */

// API Configuration
// Prefer explicit env, but keep a production-safe runtime fallback for mobile builds.
const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL;
const FALLBACK_PUBLIC_API_URL = process.env.NEXT_PUBLIC_FALLBACK_API_URL || 'https://autorapido.it';

function resolveRuntimeApiBaseUrl(): string {
  const envBase = (API_BASE_URL || '').trim();

  if (typeof window === 'undefined') return '';

  const cap = (window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  const isNativeBridge = !!cap && (typeof cap.isNativePlatform === 'function' ? cap.isNativePlatform() : true);
  const origin = (window.location?.origin || '').trim();
  const protocol = (window.location?.protocol || '').trim().toLowerCase();
  const isNativeLikeOrigin =
    protocol === 'capacitor:' ||
    protocol === 'ionic:' ||
    protocol === 'file:' ||
    origin.startsWith('capacitor://') ||
    origin.startsWith('ionic://') ||
    origin.startsWith('https://localhost');
  const shouldUseRemoteFallback = isNativeBridge || isNativeLikeOrigin;

  if (envBase) {
    // In native/mobile contexts never keep localhost-style dev backends.
    if (
      shouldUseRemoteFallback &&
      (/^http:\/\//i.test(envBase) || /^https?:\/\/(localhost|127\.0\.0\.1)(:|$)/i.test(envBase))
    ) {
      return FALLBACK_PUBLIC_API_URL;
    }
    return envBase;
  }

  if (shouldUseRemoteFallback) {
    return FALLBACK_PUBLIC_API_URL;
  }

  if (isNativeLikeOrigin) {
    return FALLBACK_PUBLIC_API_URL;
  }

  if (/^https?:\/\//i.test(origin)) {
    return origin;
  }

  return FALLBACK_PUBLIC_API_URL;
}

// Previously, this client returned mock fallback data on failed/empty API
// responses. That prevents the dashboard from reflecting the authenticated
// user's real state, so mock data has been removed.

export interface ApiError {
  message: string;
  status: number;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'user';
  avatar?: string;
}

export interface Project {
  id: string;
  name: string;
  folder: string;
  images: number;
  status: 'Pending' | 'In Progress' | 'Ready to Process' | 'Completed';
  date: string;
  created_at: string;
  updated_at?: string;
  description?: string;
  image_paths?: string[];
  /** Optional UI-only field (e.g. demo/figma mode) */
  editedBy?: string;
  /** Vehicle type (e.g. Coupé, SUV, Crossover) */
  type?: string;
  /** VIN code */
  vin?: string;
  /** License plate */
  plate?: string;
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  status: 'Active' | 'Pending';
  created_at: string;
  avatar?: string;
}

export interface Stats {
  total_projects: number;
  images_processed: number;
  active_users: number;
  storage_used: string;
}

export interface Asset {
  id: string;
  url: string;
  name: string;
}

export interface ProcessingConfig {
  project_id: string;
  bg_asset_id: string | null;
  plate_asset_id: string | null;
  bg_url: string | null;
  plate_url: string | null;
}

export interface Template {
  id: string;
  name: string;
  adjustments: Record<string, number>;
  bg_asset_id: string | null;
  plate_asset_id: string | null;
  bg_url: string | null;
  plate_url: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface Settings {
  language?: string;
  notifications?: boolean;
  autoSync?: boolean;
  showShared?: boolean;
  defaultRole?: string;
  [key: string]: string | boolean | number | null | undefined;
}

export interface MobileUploadResult {
  message: string;
  path: string;
  labels: string | null;
}

export interface MobileProcessResult {
  status: string;
  folder: string;
}

export interface DarknetDetectionPayload {
  detections?: unknown;
  stdout?: string;
  stderr?: string;
  error?: string;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = resolveRuntimeApiBaseUrl()) {
    this.baseUrl = baseUrl;
  }

  private getRequiredBaseUrl(): string {
    const base = (this.baseUrl || '').trim();
    if (!base) {
      throw {
        message: 'Backend URL not configured. Set NEXT_PUBLIC_API_URL in frontend/.env.local.',
        status: 0,
      } as ApiError;
    }
    return base.replace(/\/$/, '');
  }

  /**
   * Build an absolute URL for an asset served by the backend.
   * - If `pathOrUrl` is already absolute (http/https), it is returned as-is.
   * - If NEXT_PUBLIC_API_URL is not configured, returns the input as-is.
   */
  getAssetUrl(pathOrUrl: string): string {
    if (!pathOrUrl) return '';
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;

    // Frontend (Next.js) public assets should not be prefixed with the backend API URL.
    // Figma/static UI assets live under `/frontend/public/*` and are referenced by
    // absolute paths like `/dashboard/...`.
    const frontendPublicPrefixes = [
      '/dashboard/',
      '/cars/',
      '/camera/',
      '/edit/',
      '/team/',
      '/projects/',
      '/branding/',
      '/loginScreen/',
      '/menumobile/',
      '/figma_png/',
    ];
    if (pathOrUrl.startsWith('/') && frontendPublicPrefixes.some((p) => pathOrUrl.startsWith(p))) {
      return pathOrUrl;
    }

    const base = (this.baseUrl || '').trim().replace(/\/$/, '');
    if (!base) return pathOrUrl;

    return `${base}/${pathOrUrl.replace(/^\//, '')}`;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    timeoutMs = 5000
  ): Promise<T> {
    const baseUrl = this.getRequiredBaseUrl();
    const safeEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const url = `${baseUrl}${safeEndpoint}`;

    // Add a timeout to prevent hanging forever
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    // Get current user from localStorage for authentication
    let user = null;
    try {
      const userStr = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      user = userStr ? JSON.parse(userStr) : null;
    } catch (e) {
      console.error('Error parsing user from localStorage:', e);
    }

    const config: RequestInit = {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(user && { 'X-User-Id': user.id }), // Add user ID to headers
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);

      if (!response.ok) {
        const error: ApiError = {
          message: `HTTP error! status: ${response.status}`,
          status: response.status,
        };
        
        try {
          const errorData = await response.json();
          error.message = errorData.error || errorData.message || error.message;
        } catch {
          // If response is not JSON, use default error message
        }
        
        throw error;
      }

      return await response.json();
    } catch (error: unknown) {
      clearTimeout(timeoutId);

      const isAbortError =
        typeof error === 'object' &&
        error !== null &&
        'name' in error &&
        (error as { name?: unknown }).name === 'AbortError';

      if (isAbortError) {
        throw {
          message: `Request timed out while calling backend (${url}). Is the Flask server running?`,
          status: 408,
        } as ApiError;
      }

      const status =
        typeof error === 'object' &&
        error !== null &&
        'status' in error &&
        typeof (error as { status?: unknown }).status === 'number'
          ? (error as { status: number }).status
          : undefined;

      if (status) {
        throw error as ApiError;
      }

      const rawMessage = error instanceof Error ? error.message : String(error);

      // Helpful dev-time diagnostics:
      // - Most "CORS request did not succeed" errors are actually network issues (server not running / wrong port).
      // - If the frontend is opened from another device, 127.0.0.1/localhost will point to that device, not your dev machine.
      const isBrowser = typeof window !== 'undefined';
      const frontendHost = isBrowser ? window.location.hostname : '';
      const apiHostIsLocal = /^https?:\/\/(localhost|127\.0\.0\.1)(:|$)/i.test(baseUrl);
      const frontendIsRemote = !!frontendHost && !['localhost', '127.0.0.1'].includes(frontendHost);
      const remoteHint =
        frontendIsRemote && apiHostIsLocal
          ? ` If you're accessing the frontend from another device (host: ${frontendHost}), don't use 127.0.0.1/localhost for NEXT_PUBLIC_API_URL. Use your machine LAN IP instead.`
          : '';

      throw {
        message: `Network error while calling backend (${url}). ${rawMessage}. Check that the backend is running and that NEXT_PUBLIC_API_URL is correct.${remoteHint}`,
        status: 0,
      } as ApiError;
    }
  }

  private getUserHeaders(): HeadersInit {
    let user: User | null = null;
    try {
      const userStr = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      user = userStr ? (JSON.parse(userStr) as User) : null;
    } catch {
      user = null;
    }
    return user ? { 'X-User-Id': user.id } : {};
  }

  private async buildApiError(response: Response, fallback: string): Promise<ApiError> {
    let message = fallback;
    try {
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const payload = (await response.json()) as { error?: string; message?: string };
        message = payload.error || payload.message || fallback;
      } else {
        const text = (await response.text()).trim();
        if (text) message = text;
      }
    } catch {
      message = fallback;
    }
    return { message, status: response.status };
  }

  // Authentication APIs
  async login(email: string, password: string): Promise<User> {
    return this.request<User>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async register(email: string, password: string, name?: string): Promise<User> {
    return this.request<User>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    });
  }

  async logout(): Promise<{ message: string }> {
    return this.request('/api/auth/logout', {
      method: 'POST',
    });
  }

  async deleteAccount(): Promise<{ message: string }> {
    return this.request('/api/auth/delete', {
      method: 'DELETE',
    });
  }

  // Projects APIs
  async getProjects(): Promise<Project[]> {
    return this.request<Project[]>('/api/projects');
  }

  async getProject(projectId: string): Promise<Project> {
    return this.request<Project>(`/api/projects/${projectId}`);
  }

  async createProject(data: { name: string; folder: string; description?: string }): Promise<Project> {
    return this.request<Project>('/api/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProject(projectId: string, data: Partial<Project>): Promise<Project> {
    return this.request<Project>(`/api/projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProject(projectId: string): Promise<{ message: string }> {
    return this.request(`/api/projects/${projectId}`, {
      method: 'DELETE',
    });
  }

  // Team APIs
  async getTeam(): Promise<TeamMember[]> {
    return this.request<TeamMember[]>('/api/team');
  }

  async addTeamMember(data: { name: string; email: string; role: string }): Promise<TeamMember> {
    return this.request<TeamMember>('/api/team', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTeamMember(memberId: string, data: Partial<TeamMember>): Promise<TeamMember> {
    return this.request<TeamMember>(`/api/team/${memberId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async removeTeamMember(memberId: string): Promise<{ message: string }> {
    return this.request(`/api/team/${memberId}`, {
      method: 'DELETE',
    });
  }

  async resendTeamInvite(memberId: string): Promise<{ message: string; email_sent: boolean }> {
    return this.request(`/api/team/${memberId}/resend`, { method: 'POST' });
  }

  async acceptTeamInvite(memberId: string): Promise<{ message: string }> {
    return this.request(`/api/team/${memberId}/accept`, { method: 'POST' });
  }

  async declineTeamInvite(memberId: string): Promise<{ message: string }> {
    return this.request(`/api/team/${memberId}/decline`, { method: 'POST' });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    return this.request('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  }

  // Settings APIs
  async getSettings(): Promise<Settings> {
    return this.request<Settings>('/api/settings');
  }

  async updateSettings(data: Settings): Promise<Settings> {
    return this.request<Settings>('/api/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Stats APIs
  async getStats(): Promise<Stats> {
    return this.request<Stats>('/api/stats');
  }

  // Raw images (before processing) — used for Before/After comparison slider
  async getRawImages(projectId: string): Promise<{ raw_image_paths: string[] }> {
    return this.request(`/api/projects/${projectId}/raw-images`);
  }

  // Assets — backgrounds
  async getBackgrounds(): Promise<Asset[]> {
    return this.request('/api/assets/backgrounds');
  }

  async uploadBackground(file: File): Promise<Asset> {
    const baseUrl = this.getRequiredBaseUrl();
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${baseUrl}/api/assets/backgrounds`, {
      method: 'POST',
      headers: this.getUserHeaders(),
      body: formData,
    });
    if (!response.ok) throw await this.buildApiError(response, 'Upload failed');
    return await response.json();
  }

  async deleteBackground(filename: string): Promise<void> {
    await this.request(`/api/assets/backgrounds/${encodeURIComponent(filename)}`, { method: 'DELETE' });
  }

  // Assets — plate logos
  async getPlates(): Promise<Asset[]> {
    return this.request('/api/assets/plates');
  }

  async uploadPlate(file: File): Promise<Asset> {
    const baseUrl = this.getRequiredBaseUrl();
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${baseUrl}/api/assets/plates`, {
      method: 'POST',
      headers: this.getUserHeaders(),
      body: formData,
    });
    if (!response.ok) throw await this.buildApiError(response, 'Upload failed');
    return await response.json();
  }

  async deletePlate(filename: string): Promise<void> {
    await this.request(`/api/assets/plates/${encodeURIComponent(filename)}`, { method: 'DELETE' });
  }

  // Processing config
  async getProcessingConfig(projectId: string): Promise<ProcessingConfig> {
    return this.request(`/api/projects/${projectId}/processing-config`);
  }

  async setProcessingConfig(projectId: string, data: { bg_asset_id?: string | null; plate_asset_id?: string | null }): Promise<ProcessingConfig> {
    return this.request(`/api/projects/${projectId}/processing-config`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /** Trigger FlexSight to re-run on the project's existing raw photos.
   *  Use this after changing bg/plate via setProcessingConfig so the new
   *  background/plate are baked into the output PNGs. */
  async reprocessProject(projectId: string): Promise<{ status: string; folder?: string; message?: string }> {
    return this.request(`/api/projects/${projectId}/reprocess`, { method: 'POST' }, 15_000);
  }

  // Templates
  async getTemplates(): Promise<Template[]> {
    return this.request('/api/templates');
  }

  async createTemplate(data: { name: string; adjustments?: Record<string, number>; bg_asset_id?: string | null; plate_asset_id?: string | null }): Promise<Template> {
    return this.request('/api/templates', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateTemplate(templateId: string, data: Partial<{ name: string; adjustments: Record<string, number>; bg_asset_id: string | null; plate_asset_id: string | null }>): Promise<Template> {
    return this.request(`/api/templates/${templateId}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteTemplate(templateId: string): Promise<void> {
    await this.request(`/api/templates/${templateId}`, { method: 'DELETE' });
  }

  // Download ZIP of processed (or adjusted) images for a project
  async downloadProjectZip(projectId: string): Promise<void> {
    const baseUrl = this.getRequiredBaseUrl();
    const response = await fetch(`${baseUrl}/api/projects/${projectId}/download`, {
      headers: this.getUserHeaders(),
    });
    if (!response.ok) {
      throw await this.buildApiError(response, 'Download failed');
    }
    // Extract filename from Content-Disposition header if present
    const disposition = response.headers.get('content-disposition') || '';
    const filenameMatch = disposition.match(/filename[^;=\n]*=(?:"([^"]+)"|([^\s;]+))/);
    const filename = filenameMatch ? (filenameMatch[1] || filenameMatch[2]) : `project_${projectId}.zip`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Adjustments API — longer timeout: PIL processes all images server-side
  async applyAdjustments(
    projectId: string,
    adjustments: Record<string, number>
  ): Promise<{ adjusted_paths: string[] }> {
    return this.request(
      `/api/projects/${projectId}/adjustments`,
      { method: 'POST', body: JSON.stringify(adjustments) },
      120_000  // 2 minutes — PIL processing can take time on many images
    );
  }

  // AI Refine (SAM3 + window cleanup) — two modes:
  //   'quick'   = deterministic darkening, free, ~30s/image
  //   'premium' = gpt-image-2 polish, ~$0.25/image, ~150s/image
  async startAiRefine(projectId: string, mode: 'quick' | 'premium' = 'premium'):
    Promise<{ message: string; source?: string; mode?: string }> {
    return this.request(
      `/api/projects/${projectId}/ai-refine`,
      { method: 'POST', body: JSON.stringify({ mode }) },
      15_000
    );
  }

  async getAiRefineStatus(projectId: string): Promise<{
    state: 'idle' | 'running' | 'done' | 'error';
    done: number;
    total: number;
    cost?: number;
    mode?: 'quick' | 'premium';
    current?: string | null;
    error?: string;
    errors?: { file: string; error: string }[];
    refined_paths?: string[];
  }> {
    return this.request(`/api/projects/${projectId}/ai-refine/status`, {}, 8_000);
  }

  // File Upload APIs (existing endpoints from server.py)
  async uploadFile(folder: string, file: File): Promise<string> {
    const formData = new FormData();
    formData.append('uploaded_file', file);

    const baseUrl = this.getRequiredBaseUrl();
    const response = await fetch(`${baseUrl}/?folder=${folder}`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw {
        message: await response.text(),
        status: response.status,
      } as ApiError;
    }

    return await response.text();
  }

  async runProcessing(folder: string): Promise<string> {
    const formData = new FormData();
    formData.append('run', 'true');

    const baseUrl = this.getRequiredBaseUrl();
    const response = await fetch(`${baseUrl}/?folder=${folder}`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw {
        message: await response.text(),
        status: response.status,
      } as ApiError;
    }

    return await response.text();
  }

  async downloadProcessedImages(folder: string): Promise<Blob> {
    const baseUrl = this.getRequiredBaseUrl();
    const response = await fetch(`${baseUrl}/?folder=${folder}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw {
        message: await response.text(),
        status: response.status,
      } as ApiError;
    }

    return await response.blob();
  }

  async updateConfig(logo?: File, background?: File): Promise<string> {
    const formData = new FormData();
    formData.append('update_cfg', 'true');

    if (logo) {
      formData.append('logo', logo);
    }
    if (background) {
      formData.append('bkg', background);
    }

    const baseUrl = this.getRequiredBaseUrl();
    const response = await fetch(`${baseUrl}/`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw {
        message: await response.text(),
        status: response.status,
      } as ApiError;
    }

    return await response.text();
  }

  // Mobile APIs
  async mobileUpload(
    folder: string,
    file: File,
    options?: { labels?: File; meta?: Record<string, unknown> }
  ): Promise<MobileUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.labels) formData.append('labels', options.labels);
    if (options?.meta) formData.append('meta', JSON.stringify(options.meta));

    const baseUrl = this.getRequiredBaseUrl();
    const response = await fetch(`${baseUrl}/api/mobile/upload?folder=${encodeURIComponent(folder)}`, {
      method: 'POST',
      headers: this.getUserHeaders(),
      body: formData,
    });

    if (!response.ok) {
      throw await this.buildApiError(response, 'Mobile upload failed');
    }

    return await response.json();
  }

  async mobileProcess(folder: string): Promise<MobileProcessResult> {
    const baseUrl = this.getRequiredBaseUrl();
    const response = await fetch(`${baseUrl}/api/mobile/process?folder=${encodeURIComponent(folder)}`, {
      method: 'POST',
      headers: this.getUserHeaders(),
    });

    if (!response.ok) {
      throw await this.buildApiError(response, 'Mobile processing failed');
    }

    return await response.json();
  }

  async abortProcess(folder: string): Promise<{ status: string; folder: string }> {
    const baseUrl = this.getRequiredBaseUrl();
    const response = await fetch(`${baseUrl}/api/mobile/abort-process?folder=${encodeURIComponent(folder)}`, {
      method: 'DELETE',
      headers: this.getUserHeaders(),
    });
    if (!response.ok) throw await this.buildApiError(response, 'Abort failed');
    return await response.json();
  }

  async yoloDarknetDetect(file: File): Promise<DarknetDetectionPayload> {
    const formData = new FormData();
    formData.append('image', file);

    const baseUrl = this.getRequiredBaseUrl();
    const response = await fetch(`${baseUrl}/api/yolo/darknet`, {
      method: 'POST',
      headers: this.getUserHeaders(),
      body: formData,
    });

    if (!response.ok) {
      throw {
        message: await response.text(),
        status: response.status,
      } as ApiError;
    }

    return await response.json();
  }

  async yoloOnnxDetect(file: File): Promise<DarknetDetectionPayload> {
    const formData = new FormData();
    formData.append('image', file);

    const baseUrl = this.getRequiredBaseUrl();
    const response = await fetch(`${baseUrl}/api/yolo/onnx`, {
      method: 'POST',
      headers: this.getUserHeaders(),
      body: formData,
    });

    if (!response.ok) {
      throw {
        message: await response.text(),
        status: response.status,
      } as ApiError;
    }

    return await response.json();
  }

  async yoloDetect(file: File): Promise<DarknetDetectionPayload> {
    try {
      return await this.yoloDarknetDetect(file);
    } catch (error) {
      const status =
        typeof error === 'object' &&
        error !== null &&
        'status' in error &&
        typeof (error as { status?: unknown }).status === 'number'
          ? (error as { status: number }).status
          : 0;
      if (status === 500 || status === 501 || status === 503 || status === 0) {
        return this.yoloOnnxDetect(file);
      }
      throw error;
    }
  }
}

// Export singleton instance
export const api = new ApiClient();
export default api;
